"use client";

import useSWR from "swr";
import {
  ProductSummary,
  ProductSummaryWithViewedAt,
} from "@/common/productSummary/types";
import { useProductViewStorage } from "./useProductViewStorage";

export interface UseProductSummariesOptions {
  limit?: number;
  enabled?: boolean;
}

export interface UseProductSummariesResult {
  products: ProductSummaryWithViewedAt[];
  isLoading: boolean;
  error: Error | undefined;
}

const fetchProductSummaries = async (
  uuids: string[],
): Promise<ProductSummary[]> => {
  const response = await fetch("/api/product-summaries", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uuids }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch product summaries: ${response.status}`);
  }

  return response.json();
};

/**
 * Reads recently viewed product UUIDs from localStorage, fetches their
 * ProductSummary data from /api/product-summaries, and returns them as
 * ProductSummaryWithViewedAt sorted newest-first. Pass each product directly
 * into convertToRecentlyViewedCard to produce card props.
 */
export const useProductSummaries = (
  options: UseProductSummariesOptions = {},
): UseProductSummariesResult => {
  const { limit, enabled = true } = options;
  const { getProductViews } = useProductViewStorage();

  const views = getProductViews();
  const uuids = Array.from(
    new Set(views.map((v) => v.courseId ?? v.productId)),
  );
  const viewedAtMap = views.reduce((map, v) => {
    const key = v.courseId ?? v.productId;
    const existingViewedAt = map.get(key);
    map.set(
      key,
      existingViewedAt === undefined
        ? v.viewedAt
        : Math.max(existingViewedAt, v.viewedAt),
    );
    return map;
  }, new Map<string, number>());

  const limitedUuids = limit !== undefined ? uuids.slice(0, limit) : uuids;

  const shouldFetch = enabled && limitedUuids.length > 0;

  const { data, error, isLoading } = useSWR(
    shouldFetch ? ["api/product-summaries", ...limitedUuids] : null,
    () => fetchProductSummaries(limitedUuids),
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const products: ProductSummaryWithViewedAt[] = (data ?? [])
    .flatMap((product) => {
      const viewedAt = viewedAtMap.get(product.productUuid);
      return viewedAt !== undefined ? [{ ...product, viewedAt }] : [];
    })
    .sort((a, b) => b.viewedAt - a.viewedAt);

  return { products, isLoading, error };
};
